import { useCallback, useEffect, useRef } from 'react';

/**
 * Inserta texto EN LA POSICIÓN DEL CURSOR de un campo controlado (input o textarea),
 * reemplazando la selección si la hay, y después devuelve el foco al campo con el cursor
 * justo detrás de lo insertado.
 *
 * Mismo patrón que la Mesa de ayuda (ConversationDetail) para las respuestas rápidas: la
 * selección se lee al insertar porque el botón que dispara la inserción ya le quitó el foco
 * al campo, y el navegador conserva `selectionStart`/`selectionEnd` tras el blur. El foco y
 * el cursor se restauran en un efecto, cuando el DOM ya tiene el valor nuevo.
 *
 * @param {{ current: HTMLInputElement|HTMLTextAreaElement|null }} fieldRef
 * @param {string} value valor actual del campo
 * @param {(next: string) => void} onChange
 * @returns {(text: string) => void}
 */
export default function useCaretInsert(fieldRef, value, onChange) {
  const pendingCaret = useRef(null);

  const insert = useCallback(
    (text) => {
      if (!text) return;
      const current = value ?? '';
      const el = fieldRef.current;
      // Sin campo o sin selección legible, se agrega al final.
      const start = Number.isInteger(el?.selectionStart) ? el.selectionStart : current.length;
      const end = Number.isInteger(el?.selectionEnd) ? el.selectionEnd : start;
      pendingCaret.current = start + text.length;
      onChange(current.slice(0, start) + text + current.slice(end));
    },
    [fieldRef, value, onChange],
  );

  useEffect(() => {
    const pos = pendingCaret.current;
    if (pos == null) return;
    pendingCaret.current = null;
    const el = fieldRef.current;
    if (!el) return;
    el.focus();
    el.setSelectionRange(pos, pos);
  }, [value, fieldRef]);

  return insert;
}
